import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPermissions, getRole } from '../api';
import { anyDialogOpen } from './ui/dialogStack';

/**
 * Linear-style keyboard shortcuts.
 *
 * Active throughout the authed shell. Stays out of the way when the user is
 * typing in any input/textarea/contenteditable, and entirely while a sheet or
 * modal is open.
 *
 *   c         → /tasks?new=1 (new task — owner or assign_work)
 *   /         → /search
 *   ⌘K / Ctrl+K → /search (the one shortcut that also works while typing)
 *   g h       → /today (staff) or /home (owner)
 *   g t       → /today
 *   g s       → /staff (owner, manage_staff, or assign_work)
 *   g r       → /routines (owner)
 *   g m       → /memory (owner)
 *   g p       → /profile
 */
export function ShortcutsProvider() {
  const nav = useNavigate();

  useEffect(() => {
    let lastG = 0;
    function onKey(e: KeyboardEvent) {
      // A sheet is a piece of work in progress, and every shortcut here
      // navigates — `c` or `/` inside a half-filled loan or payroll form threw
      // it away. The panel itself holds focus, so the typing check below never
      // saw it; ⌘K is no exception, it navigates too.
      if (anyDialogOpen()) return;
      // Don't capture when typing in inputs / editable surfaces.
      const target = e.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        (target?.isContentEditable ?? false);
      // Search is the one shortcut that has to work while typing — that is
      // the point of it — and the one that uses a modifier.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (!getRole()) return;
        e.preventDefault();
        nav('/search');
        return;
      }
      if (isTyping) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const role = getRole();
      if (!role) return;
      const now = Date.now();
      const inGSequence = now - lastG < 800;

      if (e.key === 'g') {
        lastG = now;
        return;
      }

      if (inGSequence) {
        lastG = 0;
        switch (e.key) {
          case 'h':
            nav(role === 'staff' ? '/today' : '/home');
            return;
          case 't':
            nav('/today');
            return;
          case 's':
            // Router gates /staff on manage_staff OR assign_work (either is
            // a legitimate reason to see the roster), not owner alone — a
            // staff manager granted one of those permissions used to have
            // this shortcut silently no-op.
            if (role === 'owner' || getPermissions().manage_staff || getPermissions().assign_work) {
              nav('/staff');
            }
            return;
          case 'r':
            if (role === 'owner') nav('/routines');
            return;
          case 'm':
            if (role === 'owner') nav('/memory');
            return;
          case 'p':
            nav('/profile');
            return;
        }
      }

      switch (e.key) {
        case 'c':
          // Router gates /tasks on assign_work, not owner alone.
          if (role === 'owner' || getPermissions().assign_work) nav('/tasks?new=1');
          return;
        case '/':
          e.preventDefault();
          nav('/search');
          return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav]);

  return null;
}
